import { useUser } from "@/components/UserProvider";
import { Badge } from "@/components/ui/shadcn/Badge/Badge";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { getUsersOptions } from "@/lib/data/react-query";
import { useFirmPhones } from "@/lib/data/react-query/firm/firm-phones";
import type {
  ContactPhoneCall,
  ContactPhoneCallParticipant,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  formatDateTime,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Clock3,
  Pencil,
  Trash2,
  UserRound,
} from "lucide-react";
import { useState } from "react";
import {
  formatDuration,
  outcomeLabel,
  outcomeVariant,
  parsePhoneCallUtcDate,
} from "./phone-call-log-utils";

export function PhoneCallCard({
  call,
  contactId,
  contactName,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: {
  call: ContactPhoneCall;
  contactId: string;
  contactName: string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { currentUser } = useUser();
  const { data: usersData } = useQuery(getUsersOptions());
  const { data: firmPhonesData } = useFirmPhones();
  const users = usersData?.data ?? [];
  const firmPhones = firmPhonesData?.data ?? [];
  const DirectionIcon =
    call.direction === "INBOUND" ? ArrowDownLeft : ArrowUpRight;

  const participantLabel = (participant: ContactPhoneCallParticipant) => {
    if (participant.kind === "CONTACT" && participant.contactId === contactId)
      return contactName;
    if (participant.kind === "USER") {
      const user = users.find((item) => item.id === participant.userId);
      if (user) return formatUsersName(user);
      if (participant.userId === currentUser.id)
        return formatUsersName(currentUser) ?? "Current user";
    }
    if (participant.kind === "FIRM_PHONE") {
      const phone = firmPhones.find(
        (item) => item.id === participant.firmPhoneId,
      );
      if (phone) return phone.label;
    }
    return (
      formatLocalOrIntlPhoneNumber(participant.phoneNumber ?? undefined) ||
      (participant.kind === "CONTACT"
        ? "Contact"
        : participant.kind === "USER"
          ? "Firm user"
          : "Unknown participant")
    );
  };

  return (
    <article className="rounded-xl border">
      <button
        type="button"
        className="hover:bg-muted/40 flex w-full items-start gap-3 rounded-xl p-4 text-left transition-colors"
        onClick={() => {
          setExpanded((value) => !value);
        }}
        aria-expanded={expanded}
      >
        <span
          className={cn(
            "mt-0.5 grid size-9 shrink-0 place-items-center rounded-full",
            call.direction === "INBOUND"
              ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
          )}
        >
          <DirectionIcon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {call.direction === "INBOUND" ? "Incoming call" : "Outgoing call"}
            </span>
            <Badge variant={outcomeVariant(call.outcome)}>
              {outcomeLabel(call.outcome)}
            </Badge>
            {call.method === "VOIP" ? (
              <Badge variant="outline">VoIP</Badge>
            ) : null}
          </span>
          <span className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span>
              {formatDateTime(parsePhoneCallUtcDate(call.startedAt), false)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock3 className="size-3.5" />
              {formatDuration(call.startedAt, call.endedAt)}
            </span>
          </span>
        </span>
        {expanded ? (
          <ChevronUp className="mt-2 size-4" />
        ) : (
          <ChevronDown className="mt-2 size-4" />
        )}
      </button>
      {expanded ? (
        <div className="border-border border-t px-4 py-3">
          <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
            Participants
          </p>
          <div className="space-y-2">
            {call.participants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-center gap-2 text-sm"
              >
                <UserRound className="text-muted-foreground size-4" />
                <span>{participantLabel(participant)}</span>
                {participant.phoneNumber ? (
                  <span className="text-muted-foreground">
                    {formatLocalOrIntlPhoneNumber(participant.phoneNumber)}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          {call.description ? (
            <div className="mt-4 border-t pt-3">
              <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
                Description
              </p>
              <p className="text-sm whitespace-pre-wrap">{call.description}</p>
            </div>
          ) : null}
          {call.method === "MANUAL" && (canEdit || canDelete) ? (
            <div className="mt-4 flex justify-end gap-2">
              {canEdit ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onEdit}
                >
                  <Pencil /> Edit
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onDelete}
                >
                  <Trash2 /> Delete
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
